import axios from 'axios';
import { config } from 'process';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { DriverType } from '../../../interfaces/delivery-company/drivers';
import { useContext, useState } from 'react';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { UserType } from '../../../enums/users';
import { User } from '../../../interfaces/common/authentication';
import { MerchantType } from '../../../interfaces/delivery-company/merchants';
import { CustomerType } from '../../../interfaces/common/customer';
import {
  DeliveryPhase,
  DeliveryStageToStateList,
  DeliveryState,
  PaymentState,
  Workflow,
} from '../../../enums/orders';
import { regions, RegionType } from '../../../data/regions';
import AutocompleteWithThrottle from '../autocomplete-with-throttle/autocomplete-with-throttle';
import { Autocomplete, Box, Button, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { isMobile, isTablet } from 'react-device-detect';
import { InvoicePaymentMethod, MerchantOrdersInvoiceState } from '../../../interfaces/common/merchant-invoices';
import { DeliveryBatchState, PickupTaskState } from '../../../enums/tasks';
import {
  DriverInvoiceState,
  DriverInvoiceType,
} from '../../../interfaces/common/driver-invoices';
import { showCustomerAutocompleteOption } from '../../../utils/customers';
import { BranchType } from '../../../interfaces/delivery-company/branches';

export interface IFilterOption {
  display(): string;
  toLabel(): string;
  toQuery(): { key: string; value: string };
  isFrozen(): boolean;
  isQuickFilter(): boolean;
}


export class BaseFilterOption {
  fromQuickFilter: boolean;

  constructor(fromQuickFilter: boolean) {
    this.fromQuickFilter = fromQuickFilter;
  }

  public isQuickFilter(): boolean {
    return this.fromQuickFilter;
  }

}
export type ActorType = 'driver' | 'merchant' | 'customer';

export interface Actor<
  T extends { id: string; name: string; phone_number: string },
> {
  actortype: ActorType;
  val: T;
}

// ActorFilterOption represents a filter option based on driver, customer or merchant
export class ActorFilterOption<
  T extends { id: string; name: string; phone_number: string },
> extends BaseFilterOption implements IFilterOption
{
  actor: Actor<T>;
  frozen: boolean;
  customToLabel?: (item: T) => string;

  constructor(
    actor: Actor<T>,
    frozen?: boolean,
    customToLabel?: (item: T) => string,
    fromQuickFilter?: boolean
  ) {
    super(fromQuickFilter ?? false);
    this.actor = actor;
    this.frozen = frozen ?? false;
    this.customToLabel = customToLabel;
  }

  public display(): string {
    return `${this.actor.actortype}: ${this.actor.val.name} ${this.actor.val.phone_number}`;
  }

  public toQuery(): { key: string; value: string } {
    return {
      key: this.actor.actortype,
      value: this.actor.val.id,
    };
  }

  public toLabel(): string {
    if (this.customToLabel) return this.customToLabel(this.actor.val);
    return `${this.actor.val.name} ${this.actor.val.phone_number}`;
  }

  public isFrozen(): boolean {
    return this.frozen;
  }
}

export class DeliveryBatchStateFilterOption extends BaseFilterOption implements IFilterOption {
  state: DeliveryBatchState;
  frozen: boolean;

  constructor(state: DeliveryBatchState, frozen?: boolean, fromQuickFilter?: boolean) {
    super(fromQuickFilter ?? false);
    this.state = state;
    this.frozen = frozen ?? false;
  }

  public display(): string {
    return `status: ${this.state}`;
  }

  public toLabel(): string {
    return this.state;
  }

  public toQuery(): { key: string; value: string } {
    return {
      key: 'status',
      value: this.state,
    };
  }

  public isFrozen(): boolean {
    return this.frozen;
  }
}

export class PickupTaskStateFilterOption extends BaseFilterOption implements IFilterOption {
  state: PickupTaskState;
  frozen: boolean;

  constructor(state: PickupTaskState, frozen?: boolean, fromQuickFilter?: boolean) {
    super(fromQuickFilter ?? false);
    this.state = state;
    this.frozen = frozen ?? false;
  }

  public display(): string {
    return `status: ${this.state}`;
  }

  public toLabel(): string {
    return this.state;
  }

  public toQuery(): { key: string; value: string } {
    return {
      key: 'status',
      value: this.state,
    };
  }

  public isFrozen(): boolean {
    return this.frozen;
  }
}

export class BranchFilterOption extends BaseFilterOption implements IFilterOption {
  branch: BranchType
  frozen: boolean;

  constructor( branch: BranchType, frozen?: boolean, fromQuickFilter?: boolean) {
    super(fromQuickFilter ?? false);
    this.branch = branch
    this.frozen = frozen ?? false;
  }

  public display(): string {
    return `branch: ${this.branch.name}`;
  }

  public toLabel(): string {
    return this.branch.name;
  }

  public toQuery(): { key: string; value: string } {
    return {
      key: 'branch',
      value: this.branch.id.toString(),
    };
  }

  public isFrozen(): boolean {
    return this.frozen;
  }
}

export class DeliveryStateFilterOption extends BaseFilterOption implements IFilterOption {
  state: DeliveryState;
  frozen: boolean;

  constructor(state: DeliveryState, frozen?: boolean, fromQuickFilter?: boolean) {
    super(fromQuickFilter ?? false);
    this.state = state;
    this.frozen = frozen ?? false;
  }

  public display(): string {
    return `delivery state: ${this.state}`;
  }

  public toLabel(): string {
    return this.state;
  }

  public toQuery(): { key: string; value: string } {
    return {
      key: 'delivery_state',
      value: this.state,
    };
  }

  public isFrozen(): boolean {
    return this.frozen;
  }
}

export class DeliveryPhaseFilterOption extends BaseFilterOption implements IFilterOption {
  state: DeliveryPhase;
  frozen: boolean;

  constructor(state: DeliveryPhase, frozen?: boolean, fromQuickFilter?: boolean) {
    super(fromQuickFilter ?? false);
    this.state = state;
    this.frozen = frozen ?? false;
  }

  public display(): string {
    return `phase: ${this.state}`;
  }

  public toLabel(): string {
    return this.state;
  }

  public toQuery(): { key: string; value: string } {
    return {
      key: 'phase',
      value: this.state,
    };
  }

  public isFrozen(): boolean {
    return this.frozen;
  }
}

export class PaymentStateFilterOption extends BaseFilterOption implements IFilterOption {
  state: PaymentState;
  frozen: boolean;

  constructor(state: PaymentState, frozen?: boolean, fromQuickFilter?: boolean) {
    super(fromQuickFilter ?? false);
    this.state = state;
    this.frozen = frozen ?? false;
  }

  public display(): string {
    return `payment state: ${this.state}`;
  }

  public toLabel(): string {
    return this.state;
  }

  public toQuery(): { key: string; value: string } {
    return {
      key: 'payment_state',
      value: this.state,
    };
  }

  public isFrozen(): boolean {
    return this.frozen;
  }
}

export class MerchantInvoiceStateFilterOption extends BaseFilterOption implements IFilterOption {
  state: MerchantOrdersInvoiceState;
  frozen: boolean;

  constructor(state: MerchantOrdersInvoiceState, frozen?: boolean, fromQuickFilter?: boolean) {
    super(fromQuickFilter ?? false);
    this.state = state;
    this.frozen = frozen ?? false;
  }

  public display(): string {
    return `merchant invoice state: ${this.state.replaceAll('_', ' ')}`;
  }

  public toLabel(): string {
    return this.state;
  }

  public toQuery(): { key: string; value: string } {
    return {
      key: 'status',
      value: this.state,
    };
  }

  public isFrozen(): boolean {
    return this.frozen;
  }
}

export class PaymentMethodFilterOption extends BaseFilterOption implements IFilterOption {
  method: InvoicePaymentMethod;
  frozen: boolean;

  constructor(method: InvoicePaymentMethod, frozen?: boolean, fromQuickFilter?: boolean) {
    super(fromQuickFilter ?? false);
    this.method = method;
    this.frozen = frozen ?? false;
  }

  public display(): string {
    return `payment method: ${this.method}`;
  }

  public toLabel(): string {
    return this.method;
  }

  public toQuery(): { key: string; value: string } {
    return {
      key: 'payment_method',
      value: this.method,
    };
  }

  public isFrozen(): boolean {
    return this.frozen;
  }
}

export class DriverInvoiceStateFilterOption extends BaseFilterOption  implements IFilterOption {
  state: DriverInvoiceState;
  frozen: boolean;

  constructor(state: DriverInvoiceState, frozen?: boolean, fromQuickFilter?: boolean) {
    super(fromQuickFilter ?? false);
    this.state = state;
    this.frozen = frozen ?? false;
  }

  public display(): string {
    return `payment state: ${this.state.replaceAll('_', ' ')}`;
  }

  public toLabel(): string {
    return this.state;
  }

  public toQuery(): { key: string; value: string } {
    return {
      key: 'status',
      value: this.state,
    };
  }

  public isFrozen(): boolean {
    return this.frozen;
  }
}

export class DriverInvoiceTypeFilterOption extends BaseFilterOption implements IFilterOption {
  state: DriverInvoiceType;
  frozen: boolean;

  constructor(state: DriverInvoiceType, frozen?: boolean, fromQuickFilter?: boolean) {
    super(fromQuickFilter ?? false);
    this.state = state;
    this.frozen = frozen ?? false;
  }

  public display(): string {
    return `type: ${this.state.replaceAll('_', ' ')}`;
  }

  public toLabel(): string {
    return this.state;
  }

  public toQuery(): { key: string; value: string } {
    return {
      key: 'type',
      value: this.state,
    };
  }

  public isFrozen(): boolean {
    return this.frozen;
  }
}

export class StringFilterOption extends BaseFilterOption implements IFilterOption {
  value: string;
  key: string;
  frozen: boolean;

  constructor(key: string, value: string, frozen?: boolean, fromQuickFilter?: boolean) {
    super(fromQuickFilter ?? false);
    this.key = key;
    this.value = value;
    this.frozen = frozen ?? false;
  }

  public display(): string {
    return `${this.key}: ${this.value}`;
  }

  public toLabel(): string {
    return this.value;
  }

  public toQuery(): { key: string; value: string } {
    return {
      key: this.key,
      value: this.value,
    };
  }

  public isFrozen(): boolean {
    return this.frozen;
  }
}

// AsyncFetchingHandlerFunc is function that receives a user object and returns a function that fetches
// related IFilterOption items
type AsyncFetchingHandlerFunc = (config: {
  headers?: any;
}) => (query: string) => Promise<IFilterOption[]>;

// AsyncFilteringAttributes groups the necessary attributes required to render and run a filter
export interface AsyncFilteringAttributes {
  handler: AsyncFetchingHandlerFunc;
  label: string;
}

// getFilterOptions returns a list of filter options
type getValues = () => IFilterOption[];

// AsyncFilteringAttributes groups the necessary attributes required to render and run a filter
export interface FilteringAttributes {
  getValues: getValues;
}

const getHandleFetchDrivers: AsyncFetchingHandlerFunc =
  (config) =>
  async (query: string): Promise<IFilterOption[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/drivers/?page_size=10&name_or_phone=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: DriverType[];
      }>(endpoint, config);

      return response.data.results.map(
        (val: DriverType) =>
          new ActorFilterOption({
            actortype: 'driver',
            val: val,
          })
      );
    } catch (e) {
      console.log('failed to fetch drivers');
    }
    return [];
  };

const getHandleFetchMerchants: AsyncFetchingHandlerFunc =
  (config) =>
  async (query: string): Promise<IFilterOption[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/merchants/?page_size=10&name_or_phone=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: MerchantType[];
      }>(endpoint, config);

      return response.data.results.map(
        (val: MerchantType) =>
          new ActorFilterOption({
            actortype: 'merchant',
            val: val,
          })
      );
    } catch (e) {
      console.log('failed to fetch merchants');
    }
    return [];
  };

const getHandleFetchCustomers: AsyncFetchingHandlerFunc =
  (config) =>
  async (query: string): Promise<IFilterOption[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/customers/?page_size=10&name_or_phone=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: CustomerType[];
      }>(endpoint, config);

      return response.data.results.map(
        (val: CustomerType) =>
          new ActorFilterOption(
            {
              actortype: 'customer',
              val: val,
            },
            false,
            showCustomerAutocompleteOption
          )
      );
    } catch (e) {
      console.log('failed to fetch customers');
    }
    return [];
  };

const getHandleFetchBranches: AsyncFetchingHandlerFunc =
  (config) =>
  async (query: string): Promise<IFilterOption[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/branches/?page_size=10&name=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: BranchType[];
      }>(endpoint, config);

      return response.data.results.map(
        (val: BranchType) =>
          new BranchFilterOption(
            val,
            false
          )
      );
    } catch (e) {
      console.log('failed to fetch customers');
    }
    return [];
  };

const getHandleFetchDeliveryBatchStates = (): IFilterOption[] => {
  return Object.values(DeliveryBatchState).map(
    (item) => new DeliveryBatchStateFilterOption(item)
  );
};

const getHandleFetchPickupStates = (): IFilterOption[] => {
  return Object.values(PickupTaskState).map(
    (item) => new PickupTaskStateFilterOption(item)
  );
};

const getHandleFetchDeliveryStates = (): IFilterOption[] => {
  return Object.values(DeliveryState).map(
    (item) => new DeliveryStateFilterOption(item)
  );
};

const getHandleFetchDeliveryPhases = (): IFilterOption[] => {
  return Object.values(DeliveryPhase).map(
    (item) => new DeliveryPhaseFilterOption(item)
  );
};

const getHandleFetchPaymentStates = (): IFilterOption[] => {
  return Object.values(PaymentState).map(
    (item) => new PaymentStateFilterOption(item)
  );
};

const getHandleFetchMerchantOrdersInvoiceStates = (): IFilterOption[] => {
  return Object.values(MerchantOrdersInvoiceState).map(
    (item) => new MerchantInvoiceStateFilterOption(item)
  );
};

const getHandleFetchPaymentMethods = (): IFilterOption[] => {
  return Object.values(InvoicePaymentMethod).map(
    (item) => new PaymentMethodFilterOption(item)
  );
};

const getHandleFetchDriverInvoiceStates = (): IFilterOption[] => {
  return Object.values(DriverInvoiceState).map(
    (item) => new DriverInvoiceStateFilterOption(item)
  );
};

const getHandleFetchDriverInvoiceTypes = (): IFilterOption[] => {
  return Object.values(DriverInvoiceType).map(
    (item) => new DriverInvoiceTypeFilterOption(item)
  );
};

const getHandleFetchGovernorates = (): IFilterOption[] => {
  return Array.from(new Set(regions.map((item) => item.governorate))).map(
    (item) => new StringFilterOption('governorate', item)
  );
};

const getHandleFetchDistricts = (): IFilterOption[] => {
  return Array.from(new Set(regions.map((item) => item.district))).map(
    (item) => new StringFilterOption('district', item)
  );
};

const getHandleFetchWorkflows = (): IFilterOption[] => {
  return [
    new StringFilterOption('workflow', Workflow.STANDARD),
    new StringFilterOption('workflow', Workflow.RETURN),
  ];
};

export type FilterKey =
  | 'driver'
  | 'merchant'
  | 'customer'
  | 'delivery state'
  | 'payment state'
  | 'district'
  | 'governorate'
  | 'workflow'
  | 'merchant invoice status'
  | 'payment method'
  | 'driver invoice status'
  | 'driver invoice type'
  | 'phase'
  | 'pickup task state'
  | 'delivery batch state'
  | 'branch'

interface RenderFilterInputProps {
  filter: FilteringAttributes | AsyncFilteringAttributes;
  selectedFilterOptions: IFilterOption[];
  setSelectedFilterOptions: any;
}
const RenderAsyncFilterInput = ({
  filter,
  selectedFilterOptions,
  setSelectedFilterOptions,
}: RenderFilterInputProps) => {
  const { config } = useContext(AuthContext);
  return (
    <AutocompleteWithThrottle<IFilterOption>
      label={(filter as AsyncFilteringAttributes).label}
      required={false}
      fetch={(filter as AsyncFilteringAttributes).handler(config)}
      getOptionLabel={(option: IFilterOption) => option.toLabel()}
      value={null}
      fetchAllOnLoad
      onChange={(e, value) => {
        if (value) setSelectedFilterOptions([...selectedFilterOptions, value]);
      }}
    />
  );
};

const RenderButtonsFilterInput = ({
  filter,
  selectedFilterOptions,
  setSelectedFilterOptions,
}: RenderFilterInputProps) => {
  const { user, config } = useContext(AuthContext);
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        marginBottom: 2,
        height: '100%',
      }}
    >
      {(filter as FilteringAttributes)
        .getValues()
        .filter(
          (item) =>
            selectedFilterOptions.filter(
              (option) => option.toLabel() === item.toLabel()
            ).length === 0
        )
        .map((item) => (
          <Button
            size={(isMobile && !isTablet) ? 'small' : 'medium'}
            onClick={() =>
              setSelectedFilterOptions([...selectedFilterOptions, item])
            }
            key={item.toLabel()}
            variant="outlined"
          >
            {item.toLabel().replaceAll('_', ' ')} <AddIcon fontSize="small" />
          </Button>
        ))}
    </Box>
  );
};

const RenderAutocompleteFilterInput = ({
  filter,
  selectedFilterOptions,
  setSelectedFilterOptions,
}: RenderFilterInputProps) => {
  const { user, config } = useContext(AuthContext);
  const [inputVal, setInputVal] = useState<string>('');
  return (
    <Autocomplete
      disablePortal
      fullWidth
      isOptionEqualToValue={(option, value) =>
        option.display() === value.display()
      }
      onChange={(_, newValue: IFilterOption | null) => {
        if (newValue) {
          setInputVal('');
          setSelectedFilterOptions([...selectedFilterOptions, newValue]);
        }
      }}
      inputValue={inputVal}
      onInputChange={(event, newInputValue) => {
        if (event === null) return;
        setInputVal(newInputValue);
      }}
      options={(filter as FilteringAttributes)
        .getValues()
        .filter(
          (item) =>
            selectedFilterOptions.filter(
              (option) => option.toLabel() === item.toLabel()
            ).length === 0
        )}
      getOptionLabel={(option) => option.toLabel()}
      renderInput={(params) => (
        <TextField autoComplete="off" {...params} label={'Start typing...'} />
      )}
    />
  );
};

const AvailableFilters = new Map<
  FilterKey,
  (
    selectedFilterOptions: IFilterOption[],
    setSelectedFilterOptions: any
  ) => JSX.Element
>([
  [
    'driver',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderAsyncFilterInput
        key='driver'
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{
          handler: getHandleFetchDrivers,
          label: 'Search by name or phone number',
        }}
      />
    ),
  ],
  [
    'customer',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderAsyncFilterInput
        key='customer'
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{
          handler: getHandleFetchCustomers,
          label: 'Search by name or phone number',
        }}
      />
    ),
  ],
  [
    'merchant',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderAsyncFilterInput
        key='merchant'
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{
          handler: getHandleFetchMerchants,
          label: 'Search by name or phone number',
        }}
      />
    ),
  ],
  [
    'workflow',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderButtonsFilterInput
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{ getValues: getHandleFetchWorkflows, }}
      />
    ),
  ],
  [
    'phase',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderButtonsFilterInput
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{ getValues: getHandleFetchDeliveryPhases, }}
      />
    ),
  ],
  [
    'pickup task state',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderButtonsFilterInput
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{ getValues: getHandleFetchPickupStates }}
      />
    ),
  ],
  [
    'delivery batch state',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderButtonsFilterInput
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{ getValues: getHandleFetchDeliveryBatchStates }}
      />
    ),
  ],
  [
    'delivery state',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderButtonsFilterInput
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{ getValues: getHandleFetchDeliveryStates }}
      />
    ),
  ],
  [
    'payment state',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderButtonsFilterInput
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{ getValues: getHandleFetchPaymentStates }}
      />
    ),
  ],
  [
    'merchant invoice status',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderButtonsFilterInput
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{ getValues: getHandleFetchMerchantOrdersInvoiceStates }}
      />
    ),
  ],
  [
    'payment method',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderButtonsFilterInput
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{ getValues: getHandleFetchPaymentMethods }}
      />
    ),
  ],
  [
    'driver invoice status',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderButtonsFilterInput
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{ getValues: getHandleFetchDriverInvoiceStates }}
      />
    ),
  ],
  [
    'driver invoice type',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderButtonsFilterInput
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{ getValues: getHandleFetchDriverInvoiceTypes }}
      />
    ),
  ],
  [
    'governorate',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderButtonsFilterInput
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{ getValues: getHandleFetchGovernorates }}
      />
    ),
  ],
  [
    'district',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderAutocompleteFilterInput
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{ getValues: getHandleFetchDistricts }}
      />
    ),
  ],
  [
    'branch',
    (selectedFilterOptions: IFilterOption[], setSelectedFilterOptions: any) => (
      <RenderAsyncFilterInput
        key='branch'
        selectedFilterOptions={selectedFilterOptions}
        setSelectedFilterOptions={setSelectedFilterOptions}
        filter={{
          handler: getHandleFetchBranches,
          label: 'Search by branch name',
        }}
      />
    ),
  ]
]);

export const getFilters = (
  keys: string[]
): Map<
  FilterKey,
  (
    selectedFilterOptions: IFilterOption[],
    setSelectedFilterOptions: any
  ) => JSX.Element
> => {
  const res = new Map<
    FilterKey,
    (
      selectedFilterOptions: IFilterOption[],
      setSelectedFilterOptions: any
    ) => JSX.Element
  >();
  AvailableFilters.forEach((value, key) => {
    if (keys.includes(key)) res.set(key, value);
  });
  return res;
};
